// Global utilities
import moment from 'moment-timezone';
import { start } from 'nprogress';
import { nextTick } from 'vue';
import { customStorageEventName } from '@vueuse/core';

const timezone = {
    eastern: 'America/New_York'
}

moment.tz.setDefault(timezone.eastern)

export const util = {

    // Formats an integer or float for display as currency
    currency: (rate) => {
        return Number(rate).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        });
    },
    currencyWithoutFraction: (value) => {
        return Number(Math.round(value)).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
        }).replace(/(\.00)$/, '');
    },
    // Converts line breaks to HTML
    // See: https://www.php.net/manual/en/function.nl2br.php
    nl2br: (str) => {
        return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br>$2');
    },
    // Converts a string to Title Case
    titleCase: (str) => {
        if (!str)
            return '';

        str = str.toLowerCase().split(' ');
        for (var i = 0; i < str.length; i++) {
            str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
        }
        return str.join(' ');
    },

    // Formats a display name from first and last name, filtering out null/undefined values
    // Returns "Unnamed {fallbackType}" if both names are empty and fallbackType is provided
    displayClientName: (firstName, lastName, fallbackType = null) => {
        const nameParts = [firstName, lastName]
            .filter(Boolean)
            .map(name => util.titleCase(name));
        if (nameParts.length > 0) {
            return nameParts.join(' ');
        }
        return fallbackType ? `Unnamed ${util.titleCase(fallbackType)}` : '';
    },

    underscoreToSpace: (str) => {
        if (!str)
            return '';

        str = str.replace(/_/g, ' ').toLowerCase().split(' ');
        for (var i = 0; i < str.length; i++) {
            str[i] = str[i].charAt(0) + str[i].slice(1);
        }
        return str.join(' ');
    },
    // Converts a number/integer of minutes to rounded-down hours and the remainding number of minutes
    toHoursAndMinutes: (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return { hours, minutes };
    },
    // Returns a Date object from the frequently-used YYYY-MM-DD format
    makeDate: (dateString) => {
        return new Date(
            dateString.substr(0, 4),
            Number(dateString.substr(5, 2))-1,
            Number(dateString.substr(8, 2))
        );
    },
    // Converts UTC date from server to local time
    makeLocalTime: (date) => {
        let newDate = new Date(date.getTime()+date.getTimezoneOffset()*60*1000);
        let offset = date.getTimezoneOffset() / 60;
        let hours = date.getHours();
        newDate.setHours(hours - offset);
        return newDate;
    },
    getDateFromString(dateString)
    {
        return moment(dateString, 'YYYY-MM-DD HH:mm:ss').toDate();
    },
    // Excessive date formatting function
    // See: https://gist.github.com/kubiqsk/c60207a3075104df7cc1822a95053ecd
    formatDate: (formatStr, date, locale = navigator.language) => {

        const replaceChars = {
            // day
            d: function(){ return ( '0' + this.getDate() ).slice(-2) },
            D: function( locale ){ return new Intl.DateTimeFormat( locale, { weekday: 'short' } ).format( this ) },
            j: function(){ return this.getDate() },
            l: function( locale ){ return new Intl.DateTimeFormat( locale, { weekday: 'long' } ).format( this ) },
            N: function(){
                let day = this.getDay();
                return day === 0 ? 7 : day;
            },
            S: function(){
                let date = this.getDate();
                return date % 10 === 1 && date !== 11 ? 'st' : ( date % 10 === 2 && date !== 12 ? 'nd' : ( date % 10 === 3 && date !== 13 ? 'rd' : 'th' ) );
            },
            J: function(){
                let date = this.getDate();
                return this.getDate() + ( date % 10 === 1 && date !== 11 ? 'st' : ( date % 10 === 2 && date !== 12 ? 'nd' : ( date % 10 === 3 && date !== 13 ? 'rd' : 'th' ) ) );
            },
            w: function(){ return this.getDay() },
            z: function(){ return Math.floor( ( this - new Date( this.getFullYear(), 0, 1 ) ) / 86400000 ) },
            // week
            W: function(){
                let target = new Date( this.valueOf() );
                let dayNr = ( this.getDay() + 6 ) % 7;
                target.setDate( target.getDate() - dayNr + 3 );
                let firstThursday = target.valueOf();
                target.setMonth( 0, 1 );
                if( target.getDay() !== 4 ){
                    target.setMonth( 0, 1 + ( ( 4 - target.getDay() ) + 7 ) % 7 );
                }
                return Math.ceil( ( firstThursday - target ) / 604800000 ) + 1;
            },
            // month
            F: function( locale ){ return new Intl.DateTimeFormat( locale, { month: 'long' } ).format( this ) },
            m: function(){ return ( '0' + ( this.getMonth() + 1 ) ).slice(-2) },
            M: function( locale ){ return new Intl.DateTimeFormat( locale, { month: 'short' } ).format( this ) },
            n: function(){ return this.getMonth() + 1 },
            t: function(){
                let year = this.getFullYear();
                let nextMonth = this.getMonth() + 1;
                if( nextMonth === 12 ){
                    year = year++;
                    nextMonth = 0;
                }
                return new Date( year, nextMonth, 0 ).getDate();
            },
            // year
            L: function(){
                let year = this.getFullYear();
                return year % 400 === 0 || ( year % 100 !== 0 && year % 4 === 0 ) ? 1 : 0;
            },
            o: function(){
                let date = new Date( this.valueOf() );
                date.setDate( date.getDate() - ( ( this.getDay() + 6 ) % 7 ) + 3 );
                return date.getFullYear();
            },
            Y: function(){ return this.getFullYear() },
            y: function(){ return ( '' + this.getFullYear() ).slice(-2) },
            // time
            a: function(){ return this.getHours() < 12 ? 'am' : 'pm' },
            A: function(){ return this.getHours() < 12 ? 'AM' : 'PM' },
            K: function(){ return this.getHours() < 12 ? 'AM' : 'PM' },
            B: function(){
                return ( '00' + Math.floor( ( ( ( this.getUTCHours() + 1 ) % 24 ) + this.getUTCMinutes() / 60 + this.getUTCSeconds() / 3600 ) * 1000 / 24 ) ).slice(-3);
            },
            g: function(){ return this.getHours() % 12 || 12 },
            G: function(){ return this.getHours() },
            h: function(){ return ( '0' + ( this.getHours() % 12 || 12 ) ).slice(-2) },
            H: function(){ return ( '0' + this.getHours() ).slice(-2) },
            i: function(){ return ( '0' + this.getMinutes() ).slice(-2) },
            s: function(){ return ( '0' + this.getSeconds() ).slice(-2) },
            v: function(){ return ( '00' + this.getMilliseconds() ).slice(-3) },
            // Timezone
            e: function(){ return Intl.DateTimeFormat().resolvedOptions().timeZone },
            I: function(){
                let DST = null;
                for( let i = 0; i < 12; ++i ){
                    let d = new Date( this.getFullYear(), i, 1 );
                    let offset = d.getTimezoneOffset();
                    if( DST === null ){
                        DST = offset;
                    }else if( offset < DST ){
                        DST = offset;
                        break;
                    }else if( offset > DST ){
                        break;
                    }
                }
                return ( this.getTimezoneOffset() === DST ) | 0;
            },
            O: function(){
                let timezoneOffset = this.getTimezoneOffset();
                return ( -timezoneOffset < 0 ? '-' : '+' ) + ( '0' + Math.floor( Math.abs( timezoneOffset / 60 ) ) ).slice(-2) + ( '0' + Math.abs( timezoneOffset % 60 ) ).slice(-2);
            },
            P: function(){
                let timezoneOffset = this.getTimezoneOffset();
                return ( -timezoneOffset < 0 ? '-' : '+' ) + ( '0' + Math.floor( Math.abs( timezoneOffset / 60 ) ) ).slice(-2) + ':' + ( '0' + Math.abs( timezoneOffset % 60 ) ).slice(-2);
            },
            T: function( locale ){
                let timeString = this.toLocaleTimeString( locale, { timeZoneName: 'short' } ).split(' ');
                let abbr = timeString[ timeString.length - 1 ];
                return abbr == 'GMT+1' ? 'CET' : ( abbr == 'GMT+2' ? 'CEST' : abbr );
            },
            Z: function(){ return -this.getTimezoneOffset() * 60 },
            // Full Date/Time
            c: function(){ return this.format('Y-m-d\\TH:i:sP') },
            r: function(){ return this.format('D, d M Y H:i:s O') },
            U: function(){ return Math.floor( this.getTime() / 1000 ) }
        }

		return formatStr.replace( /(\\?)(.)/g, function( _, esc, chr ){
			return esc === '' && replaceChars[ chr ] ? replaceChars[ chr ].call( date, locale ) : chr
		});
	},
    // Human-readable shift length string
    friendlyShiftLength: (shiftStart, shiftEnd) => {
        const start = util.getDateFromString(shiftStart).valueOf();
        const end = util.getDateFromString(shiftEnd).valueOf();
        const totals = util.toHoursAndMinutes((end-start) / 60000);
        let string = totals.hours + ' hours';
        if (totals.minutes > 0) {
            string += ', ' + totals.minutes + 'min';
        }
        return string;
    },
    // Convert minutes to hours (I know... I know...)
    toHours: (totalMinutes) => {
        return totalMinutes / 60;
    },
    // Return shift pay in number format, given start and end times, as well as exact rate
    shiftTotal: (shiftStart, shiftEnd, rate) => {
        const start = util.getDateFromString(shiftStart).valueOf();
        const end = util.getDateFromString(shiftEnd).valueOf();
        return Number(util.toHours((end-start) / 60000) * rate).toFixed(2);
    },
    // Return trip value based on mileage rate in global config, in cents
    tripTotal: (trip, config) => {
        return Number(trip.distance * (config.mileageRate * 0.01));
    },
    // Adds a day of padding to a date
    addDayPadding: (date) => {
        var result = new Date(date);
        result.setDate(result.getDate() + 1);
        return result;
    },
    // Returns a friendly date string
    makeFriendlyDate: (dateString, config) => {
        return util.formatDate(config.friendlyDateFormat, moment(dateString, 'YYYY-MM-DD HH:mm:ss').toDate());
    },
    makeFriendlyDateTime: (dateTimeString) => {
        return util.formatDate('M d, Y h:i A', moment(dateTimeString).toDate());
    },
    // Returns a compact friendly date string
    makeFriendlyShortDate: (dateString, config) => {
        return util.formatDate(config.friendlyShortDateFormat, moment(dateString, 'YYYY-MM-DD HH:mm:ss').toDate());
    },
    // Returns the total value of the shifts based on their included rates
    sumShifts: (shifts, rates) => {
        let total = 0;
        shifts.forEach(shift => {
            total += Number(util.shiftTotal(shift.start, shift.end, rates[shift.rate_id].amount));
        }, rates);
        return total;
    },
    // Returns the total value of the trips based on the mileage rate in global config
    sumMileage: (trips, config) => {
        let total = 0;
        trips.forEach(trip => {
            total += Number(util.tripTotal(trip, config));
        });
        return total;
    },
    // Returns the total value of the expenses because math is hard
    sumExpenses: (expenses) => {
        let total = 0;
        expenses.forEach(expense => {
            total += Number(expense.amount);
        });
        return total;
    },
    // Returns the remaining value of the shifts after the standard deduction rate in global config
    shiftsDeductions: (shifts, config, rates) => {
        return util.sumShifts(shifts, rates) * (config.deduction / 100);
    },
    // Returns the invoice total based on all data provided and the global config
    invoiceTotal: (invoice, config, rates) => {
        let total = 0;
        if (invoice.shifts && invoice.shifts.length > 0) {
            total += Number((util.sumShifts(invoice.shifts, rates) - util.shiftsDeductions(invoice.shifts, config, rates)));
        }
        if (invoice.trips && invoice.trips.length > 0) {
            total += Number(util.sumMileage(invoice.trips, config));
        }
        if (invoice.expenses && invoice.expenses.length > 0) {
            total += Number(util.sumExpenses(invoice.expenses));
        }
        return total;
    },
    // Returns an object with keys determined by the second parameter
    // Used for remapping all invoices to an object structured by period_id instead of invoice_id
    convertArrayToObject: (array, key) => {
        const result = {};
        for (let i = 0; i < array.length; i++) {
            const item = array[i];
            if (item !== undefined && key in item) {
                result[item[key]] = item;
            }
        }
        return result;
    },
    // Shallow, order-sensitive array equality for primitives (coerces to string)
    arraysEqual: (a = [], b = []) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false
        if (a.length !== b.length) return false
        for (let i = 0; i < a.length; i++) {
            if (String(a[i]) !== String(b[i])) return false
        }
        return true
    },
    // Order-insensitive equality comparing the unique elements of both arrays.
    // Treats arrays as sets of unique values (duplicates ignored).
    // Returns true when the set of unique values in `a` equals the set of unique values in `b`.
    arraysHaveSameUniqueElements: (a = [], b = []) => {
        if (!Array.isArray(a) || !Array.isArray(b)) return false
        const ua = Array.from(new Set((a || []).map(x => String(x))))
        const ub = Array.from(new Set((b || []).map(x => String(x))))
        if (ua.length !== ub.length) return false
        const setB = new Set(ub)
        return ua.every(x => setB.has(x))
    },
    // Escape any errant HTML in a user-provided string
    escapeString: (str) => {
        var map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;'
        };
        return str.replace(/[&<>"']/g, function(m) { return map[m]; });
    },
    decodeString: (str) => {
        var map = {
            '&amp;': '&',
            '&lt;': '<',
            '&gt;': '>',
            '&quot;': '"',
            '&#039;': "'"
        };
        return str.replace(/&amp;|&lt;|&gt;|&quot;|&#039;/g, function(m) {return map[m];});
    },
    // Comparison function for sorting shifts
    shiftCompare: (a, b) => {
        if ( a.start < b.start ){
          return -1;
        }
        if ( a.start > b.start ){
          return 1;
        }
        return 0;
    },
    getOffset: (el) => {
        const rect = el.getBoundingClientRect();
        return {
            left: rect.left + window.scrollX,
            top: rect.top + window.scrollY
        };
    },
    scrollToElement: (id) => {
        // const el = document.getElementById(target);
        // if (el) {
        //     el.scrollIntoView({ block: "center", inline: "nearest", behavior: "instant" });
        // }

        const yOffset = -80;
        const element = document.getElementById(id);
        if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    },
    scrollIntoView: (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
        }
    },
    scrollToTop: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    listenforEscapeKeyPress: (callback) => {
        const handler = (event) => {
            if (event.key === 'Escape' || event.key === 'Esc') {
                callback();
            }
        };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    },
    clearFocus: () => {
        document.activeElement?.blur()
    },
    addDashesToPhone: (f) => {
        if (!f) return false;
        let r = /(\D+)/g,
            npa = '',
            nxx = '',
            last4 = '';
        f = String(f).replace(r, '');
        npa = f.substr(0, 3);
        nxx = f.substr(3, 3);
        last4 = f.substr(6, 4);
        return npa + '-' + nxx + '-' + last4;
    },
    // toLocalDateString: (zTime) => {
    //     const utcDate = moment(zTime);
    //     const localDate = utcDate.local();
    //     return localDate.format(util.date.format);
    // },
    getQueryParam: (name) => {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        return urlParams.get(name)
    },
    stringifyAddressObject: (address) => {
        return address.address + ', ' + address.city + ', ' + address.state + ' ' + address.zip;
    },
    objectifyAddressString: (address) => {
        address = decodeURIComponent(address)
        address.split(', ')
        let splitAddress = address.split(', ')
        return {
            address: splitAddress[0] ? splitAddress[0] : null,
            city: splitAddress[1] ? splitAddress[1] : null,
            state: splitAddress[2] ? splitAddress[2].split(' ')[0] : null,
            zip: splitAddress[2] ? splitAddress[2].split(' ')[1] : null
        };
    },
    isEmpty: (text) => {
        return text == null || text == '' || /^\s*$/.test(text);
    },
    // Simple email validation using a permissive regex.
    isValidEmail: (email) => {
        if (!email) return false
        const simpleEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
        return simpleEmailPattern.test(String(email))
    },
    moment: moment,
    date: {
        timezone: timezone,
        day_in_week: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
        date_format: 'YYYY-MM-DD',
        date_time_format: 'YYYY-MM-DD HH:mm:ss',
        time_format: 'HH:mm:ss',
        time_with_tz_format: 'HH:mm:ssZ',
        date_time_with_tz_format: 'YYYY-MM-DDTHH:mm:ssZ',
        date_display_format: 'MM/DD/YYYY',
        isWeekend: (day_of_week) => ['sunday', 'saturday'].includes(day_of_week),
        toDateString: (dateObject) => {
            return moment(dateObject).format(util.date.date_format)
        },
        toUtcTime: (timeString) => {
            return moment(timeString, util.date.time_with_tz_format).utc().format(util.date.time_with_tz_format)
        },
        momentToUtcDateTime: (dateObj) => {
            return moment(dateObj).utc().format(util.date.date_time_with_tz_format)
        },
        dateToUtcDateTime: (date_string, start_of_day = true) => {
            const momentDate = moment(date_string, util.date.date_format)
            if (start_of_day) {
                return momentDate.startOf('day').utc().format(util.date.date_time_with_tz_format)
            }

            return momentDate.endOf('day').utc().format(util.date.date_time_with_tz_format)
        },
        toEasternTime: (timeString) => {
            return moment(timeString, util.date.time_with_tz_format).format(util.date.time_with_tz_format)
        },
        today: () => {
            return moment().format(util.date.date_format)
        },
        lastSunday: (dateString = null) => {
            const date = dateString ? moment(dateString, util.date.date_format) : moment()
            return date.day(0).startOf('day').format(util.date.date_format)
        },
        nextSunday: (dateString = null) => {
            const date = dateString ? moment(dateString, util.date.date_format) : moment()
            return date.startOf('day').endOf('week').add(1,'days').format(util.date.date_format)
        },
        isDayOfWeek: (value, day_of_week) => {
            const date = moment(value, util.date.date_format)
            return date.format('dddd').toLowerCase() == day_of_week
        },
        toString: (dateObject) => {
            return moment(dateObject).format(util.date.date_format)
        },
        toMinute: (time) => {
            const [hours, minutes] = time.split(':').map(Number)
            const totalMinutes = (hours * 60) + minutes
            return totalMinutes
        },
        nextDayOfWeek: (day_of_week) => {
            const dayArr = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
            const currentIndex = dayArr.findIndex(day => day == day_of_week)
            return dayArr[(currentIndex + 1) % dayArr.length]
        },
        nextDayOfWeekDate: (start_date, day_of_week) => {
            if (!start_date) {
                return null
            }

            const date = moment(start_date, util.date.date_format)
            if (date.format('dddd').toLowerCase() === day_of_week.toLowerCase()) {
                return date.format(util.date.date_format)
            }

            const nextDate = date.clone().day(day_of_week)
            if (nextDate.isBefore(date, 'day')) {
                nextDate.add(1, 'week')
            }

            return nextDate.format(util.date.date_format)
        },
        previousDayOfWeek: (day_of_week) => {
            const dayArr = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
            const currentIndex = dayArr.findIndex(day => day == day_of_week)
            return dayArr[(currentIndex - 1) % dayArr.length]
        },
        previousDayOfWeekDate: (start_date, day_of_week) => {
            if (!start_date) {
                return null
            }

            const date = moment(start_date, util.date.date_format)
            if (date.format('dddd').toLowerCase() === day_of_week.toLowerCase()) {
                return date.format(util.date.date_format)
            }

            const prevDate = date.clone().day(day_of_week)
            if (prevDate.isAfter(date, 'day')) {
                prevDate.subtract(1, 'week')
            }

            return prevDate.format(util.date.date_format)
        },
        nextDate: (start_date, day = 1) => {
            if (!start_date) {
                return null
            }

            const date = moment(start_date, util.date.date_format)
            date.add(day, 'days')

            return date.format(util.date.date_format)
        },
        nextUtcDateTime: (start_date, day = 1, start_of_day = true) => {
            if (!start_date) {
                return null
            }

            const date = moment(start_date, util.date.date_format)
            date.add(day, 'days')

            if (start_of_day) {
                return date.startOf('day').utc().format(util.date.date_time_with_tz_format)
            }

            return date.endOf('day').utc().format(util.date.date_time_with_tz_format)
        },
        previousDate: (start_date, day = 1) => {
            if (!start_date) {
                return null
            }

            const date = moment(start_date, util.date.date_format)
            date.subtract(day, 'days')

            return date.format(util.date.date_format)
        },
        generateNext7MomentDays: (start_date) => {
            const date = start_date ? moment(start_date, util.date.date_format) : moment()
            const next7Days = [date]

            for (let i = 1; i < 7; i++) {
                next7Days.push(date.clone().add(i, 'days') )
            }

            return next7Days
        },
        dateDiff: (start_date, end_date) => {
            const startDate = moment(start_date, util.date.date_format)
            const endDate = moment(end_date, util.date.date_format)
            return endDate.diff(startDate, 'days')
        },
        getDayRemainMinute: (value, format = 'HH:mm:ss') => {
            const timeMoment = moment(value, format)
            const midnightMoment = timeMoment.clone().startOf('day')

            return 24 * 60 - (timeMoment.diff(midnightMoment, 'minutes'))
        },
        to12CycleTime: (value, format = 'HH:mm:ss') => {
            const timeMoment = moment(value, format)
            return timeMoment.format('hh:mm A')
        },
        to24CycleTime: (value, format = 'HH:mm:ss') => {
            const timeMoment = moment(value, format)
            return timeMoment.format('HH:mm')
        },
        to12CycleTimeParts: (value, format = 'HH:mm:ss') => {
            const timeMoment = moment(value, format)
            return [ timeMoment.format('hh:mm'), timeMoment.format('A') ];
        },
        timeAfterMinute: (value, minute, format = 'HH:mm:ss') => {
            const timeMoment = moment(value, format)
            timeMoment.add(minute, 'minutes')

            return timeMoment.format(format)
        },
        generateTimeFrame: (anchorDateString, number_of_week) => {
            const anchorDate = moment(anchorDateString, util.date.date_format)
            const lowerBoundDate = anchorDate.clone().subtract(number_of_week, 'weeks')
            const upperBoundDate = anchorDate.clone().add(number_of_week, 'weeks')

            return [
                lowerBoundDate.format(util.date.date_format),
                upperBoundDate.format(util.date.date_format)
            ]
        },
        getWeekText: (dateString) => {
            const inputDate = moment(dateString, util.date.date_format)

            const today = moment().startOf('day');
            const lastSunday = today.clone().startOf('week')
            const nextSaturday = today.clone().endOf('week')

            const pastSunday = lastSunday.clone().subtract(1, 'week').startOf('week')
            const pastSaturday = lastSunday.clone().subtract(1, 'day')

            const nextSunday = nextSaturday.clone().add(1, 'day').startOf('week')
            const nextNextSaturday = nextSaturday.clone().add(1, 'week')

            if (inputDate.isSameOrAfter(pastSunday) && inputDate.isSameOrBefore(pastSaturday)) {
                return 'Last Week'
            }

            if (inputDate.isSameOrAfter(lastSunday) && inputDate.isSameOrBefore(nextSaturday)) {
                return 'This Week'
            }

            if (inputDate.isSameOrAfter(nextSunday) && inputDate.isSameOrBefore(nextNextSaturday)) {
                return 'Next Week'
            }

            const weekStart = inputDate.clone().startOf('week').format(util.date.date_display_format)
            const weekEnd = inputDate.clone().endOf('week').format(util.date.date_display_format)
            return `${weekStart} - ${weekEnd}`
        },
        getTimeRangeText: (day_of_week, start_time, duration = 0) => {
            const formattedStart = `${day_of_week} ${start_time}`;

            const startMoment = moment(formattedStart, 'dddd HH:mm:ss');
            const endMoment = startMoment.clone().add(duration, 'minutes')

            const formattedStartTime = startMoment.format('h:mma')
            const formattedEndTime = endMoment.format('h:mma')

            const shortDayOfWeek = moment().day(day_of_week).format('ddd')

            const nextDay = endMoment.date() !== startMoment.date()

            let result = `${shortDayOfWeek} ${formattedStartTime}`
            if (nextDay) {
                result += `-${endMoment.format('ddd')} ${formattedEndTime}`
            } else {
                result += `-${formattedEndTime}`;
            }

            return result
        },
        durationToHour: (duration) => {
            const hour = Math.floor(duration / 60)
            const minute = duration % 60

            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
        },
        durationToHours: (minutes) => {
            const hours = minutes / 60
            return hours % 1 === 0 ? hours : parseFloat(hours.toFixed(2))
        },
        isSameDayOfWeek: (dayString1, dayString2) => {
            const momentObj1 = moment(dayString1, util.date.date_format)
            const momentObj2 = moment(dayString2, util.date.date_format)

            return momentObj1.format('dddd') == momentObj2.format('dddd')
        },
        findStartDayBetweenRange: (day_of_week, start_date, end_date) => {
            const startDate = moment(start_date, util.date.date_format)
            const endDate = end_date ? moment(end_date, util.date.date_format) : moment().add(1, 'month')

            let currentDate = startDate.clone()
            while (currentDate.isSameOrBefore(endDate, 'day')) {
                if (currentDate.format('dddd').toLowerCase() === day_of_week) {
                    return currentDate.format(util.date.date_format)
                }
                currentDate.add(1, 'day')
            }

            return null
        },
        formatTo24HourTime: (hour) => {
            return `${hour.toString().padStart(2, '0')}:00:00`
        }
    },
    client: {
    },
    schedule: {
        client_date_time_input_format: 'YYYY-MM-DD HH:mm',
        toClientScheduleSupportShift(shift) {
            const startDateTimeObj = moment(shift.start_datetime, util.date.date_time_with_tz_format)
            shift.start_date = startDateTimeObj.format(util.date.date_format)
            shift.start_time = startDateTimeObj.format(util.date.time_format)

            if (shift.end_datetime) {
                const endDateTimeObj = moment(shift.end_datetime, util.date.date_time_with_tz_format)
                const endDateTimeObjFromStartDate = startDateTimeObj.clone().add(shift.duration, 'minutes')

                if (endDateTimeObjFromStartDate.isSame(endDateTimeObj)) {
                    shift.end_date = startDateTimeObj.format(util.date.date_format)
                } else {
                    shift.end_date = endDateTimeObj.format(util.date.date_format)
                }
            } else {
                shift.end_date = null
            }

            return shift
        },
        isDateOverlap: (sunday, day_of_week, start_date, end_date, start_time, duration) => {
            const sundayDate = moment(sunday, util.date.date_format)
            const nextSundayDate = sundayDate.clone().add(7, 'days')
            const startDate = moment(start_date, util.date.date_format)
            let endDate = null
            if (end_date) {
                if (util.date.isSameDayOfWeek(start_date, end_date)) {
                    endDate = moment(`${end_date} ${start_time}`, util.date.date_time_format).add(duration, 'minutes')
                } else {
                    endDate = moment(`${end_date} ${start_time}`, util.date.date_time_format).add(duration, 'minutes').subtract(1, 'day')
                }
            }
            const currentDayOfWeekDate = moment(util.date.nextDayOfWeekDate(sunday, day_of_week), util.date.date_format)


            if (endDate != null ) {
                if (endDate.isSame(sundayDate)) {
                    return false
                }
                if (endDate.isSame(sundayDate, 'day')) {
                    return true
                }
            }

            if (startDate.isAfter(currentDayOfWeekDate) || (endDate != null && endDate.isBefore(currentDayOfWeekDate))) {
                return false
            }

            if (startDate.isSameOrBefore(sundayDate)) {
                if (endDate == null || endDate.isSameOrAfter(sundayDate)) {
                    return true
                }
            }

            if (startDate.isAfter(sundayDate) && startDate.isBefore(nextSundayDate)) {
                return true
            }

            return false
        },
        isTimeOverlap: (start_time_a, duration_a, start_time_b, duration_b) => {
            const start_moment_a = moment(start_time_a, 'HH:mm:ss')
            const end_moment_a = start_moment_a.clone().add(duration_a, 'minutes')
            const start_moment_b = moment(start_time_b, 'HH:mm:ss')
            const end_moment_b = start_moment_b.clone().add(duration_b, 'minutes')

            if (start_moment_a.isBetween(start_moment_b, end_moment_b, undefined, '[)')
                || start_moment_b.isBetween(start_moment_a, end_moment_a, undefined, '[)')) {
                return true;
            }

            return false;
        },
        is2DaysTime: (start_time, duration) => {
            const timeMoment = moment.tz(start_time, 'HH:mm:ss', timezone.eastern)
            const midnightMoment = timeMoment.clone().startOf('day')

            const minutesFromMidnight = timeMoment.diff(midnightMoment, 'minutes')
            return (minutesFromMidnight + duration) > 24 * 60
        },
        span: (duration) => {
            return Math.floor(duration / 15) * 3
        },
        sizeOf: (block) => {
            let span = util.schedule.span(block.duration)

            if (span > 138) { return 'xl'; }
            if (span > 116) { return 'lg'; }
            if (span > 84) { return 'md'; }
            if (span >= 48) { return 'sm'; } // 4 hours
            if (span >= 36) { return 'xs'; } // 3 hours
            if (span >= 24) { return '2xs'; } // 2 hours
            if (span >= 12) { return '3xs'; } // 1 hour
            if (span > 0) { return '4xs'; } // sub 1-hour, min size
        },
        rateOfWithoutFraction: (block) => {
            if (block.charge_rate_amount) {
                return util.currencyWithoutFraction(block.charge_rate_amount);
            }
            if (block.min_charge_rate_amount && !block.max_charge_rate_amount) {
                return util.currencyWithoutFraction(block.min_charge_rate_amount);
            }
            if (!block.min_charge_rate_amount && block.max_charge_rate_amount) {
                return util.currencyWithoutFraction(block.max_charge_rate_amount);
            }
            if (block.min_charge_rate_amount && block.max_charge_rate_amount) {
                return util.currencyWithoutFraction(block.min_charge_rate_amount) + '-' + util.currencyWithoutFraction(block.max_charge_rate_amount);
            }
            return undefined;
        },
        rateOf: (block) => {
            if (block.charge_rate_amount) {
                return util.currency(block.charge_rate_amount);
            }
            if (block.min_charge_rate_amount && !block.max_charge_rate_amount) {
                return util.currency(block.min_charge_rate_amount);
            }
            if (!block.min_charge_rate_amount && block.max_charge_rate_amount) {
                return util.currency(block.max_charge_rate_amount);
            }
            if (block.min_charge_rate_amount && block.max_charge_rate_amount) {
                return util.currency(block.min_charge_rate_amount) + '-' + util.currency(block.max_charge_rate_amount);
            }
            return undefined;
        },
        start: (time) => {
            const timeMoment = moment(time, 'HH:mm:ss')
            const midnightMoment = moment().startOf('day')
            return 2 +  Math.floor(timeMoment.diff(midnightMoment, 'minutes') / 15) * 3
        },
        startWithTz: (time) => {
            const timeMoment = moment(time, util.date.time_with_tz_format)
            const midnightMoment = timeMoment.clone().startOf('day')
            return 2 +  Math.floor(timeMoment.diff(midnightMoment, 'minutes') / 15) * 3
        },
        getWeekSpecificDate: (sunday_string, day_of_week) => {
            const sundayDate = moment(sunday_string, util.date.date_format);

            const targetDayIndex = moment().day(day_of_week).day();
            const targetDate = sundayDate.day(targetDayIndex);

            return targetDate.format(util.date.date_format);
        },
        hasFutureReOccurrence: (sunday_string, start_date_string, end_date_string) => {
            if (end_date_string == null) {
                return true
            }

            const sundayDate = moment(sunday_string, util.date.date_format)
            const nextSundayDate = sundayDate.clone().add(7, 'days')
            const startDate = moment(start_date_string, util.date.date_format)
            const endDate = moment(end_date_string, util.date.date_format)

            if (
                startDate.isBefore(sundayDate)
                && (endDate.diff(startDate, 'days') / 7) >= 2
                && endDate.isSameOrAfter(nextSundayDate)) {
                return true;
            }

            if (
                startDate.isSameOrAfter(sundayDate)
                && (endDate.diff(startDate, 'days') / 7) >= 1
                && endDate.isSameOrAfter(nextSundayDate)) {
                return true;
            }

            return false
        },
        getChargeRateAmount: (caregiver_id, day_of_week, charge_duration_rate_type, charge_rate_list) => {
            let type = ['sunday', 'saturday'].includes(day_of_week) ? 'weekend' : (!day_of_week ? '' : 'weekday')
            type += `_${charge_duration_rate_type}`
            const index = charge_rate_list.findIndex(item => (item.type === type && item.caregiver_id == caregiver_id))

            if (index > -1) {
                return charge_rate_list[index].amount
            }

            return null
        },
        getShiftDurationWithinRage: (day_of_week, start_date, end_date, duration_per_occurrence) => {
            let nextDayOfWeek = util.date.nextDayOfWeekDate(start_date, day_of_week)
            let duration = 0
            while (nextDayOfWeek < end_date) {
                duration += duration_per_occurrence
                const nextDate = util.date.nextDate(nextDayOfWeek)
                nextDayOfWeek = util.date.nextDayOfWeekDate(nextDate, day_of_week)
            }

            return duration
        },
        momentAndDurationToRangeTime: (start_time, duration) => {
            let end_time = start_time.clone().add(duration, 'minutes')
            return [
                {
                    hours: start_time.format('H'),
                    minutes: start_time.format('m')
                },
                {
                    hours: end_time.format('H'),
                    minutes: end_time.format('m')
                }
            ];
        },
        //Don't use this for start time without timezone
        shiftToBlock: (shift, originalShift, schedule_start_date) => {
            const block = {
                ...shift,
                total_duration: shift.duration,
                'start_date': util.schedule.getWeekSpecificDate(schedule_start_date, shift.day_of_week)
            }
            // delete block['day_of_week']

            if (util.schedule.is2DaysTime(shift.start_time, shift.duration)) {
                const dayRemainMinute = util.date.getDayRemainMinute(shift.start_time, util.date.time_with_tz_format)

                if (!util.date.isDayOfWeek(shift.start_date, 'saturday')) {
                    const data = [{
                        ...block,
                        duration: dayRemainMinute,
                        continuation_duration: shift.duration - dayRemainMinute,
                        continues: true
                    }]
                    data.push({
                        ...block,
                        start_time: '00:00:00',
                        shift_start_time: shift.start_time,
                        start_date: util.date.nextDate(block.start_date),
                        day_of_week: util.date.nextDayOfWeek(block.day_of_week),
                        duration: shift.duration - dayRemainMinute,
                        continuation: true
                    })
                    return data
                }

                const data = []

                if (shift.end_date == null || schedule_start_date < shift.end_date) {
                    data.push({
                        ...block,
                        duration: dayRemainMinute,
                        continuation_duration: shift.duration - dayRemainMinute,
                        continues: true
                    })
                }

                const endDateWithTime = shift.end_date == null ? null : moment(shift.end_date, util.date.date_format).add(shift.duration, 'minutes').format(util.date.date_format)
                if (schedule_start_date > shift.start_date && (endDateWithTime == null || schedule_start_date <= endDateWithTime)) {
                    if (!shift.id || schedule_start_date != util.date.lastSunday()) {
                        data.push({
                            ...block,
                            start_time: '00:00:00',
                            shift_start_time: shift.start_time,
                            day_of_week: 'sunday',
                            start_date: schedule_start_date,
                            duration: shift.duration - dayRemainMinute,
                            continuation: true
                        })
                    } else {
                        // Left over from week before current week is not editable so we use original shift
                        data.push({
                            ...originalShift,
                            start_time: '00:00:00',
                            shift_start_time: originalShift.start_time,
                            day_of_week: 'sunday',
                            start_date: schedule_start_date,
                            duration: originalShift.duration - util.date.getDayRemainMinute(originalShift.start_time),
                            continuation: true
                        })
                    }
                }
                return data;
            }

            return [block];
        }
    },
    getSmallerItem: (a, b) => {
        return (a < b) ? a : b;
    },
    getLargerItem: (a, b) => {
        return (a > b) ? a : b;
    },
    downloadFile: async (url, filename = 'download') => {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
    },
    checkUrlExists: async (url) => {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            if (!response.ok) {
                throw new Error(`URL check failed: ${response.status} ${response.statusText}`);
            }
            return true;
        } catch (error) {
            throw new Error(`Preview file not found or inaccessible`);
        }
    }
}

/**
 * Expands the collapsible container of a section without scrolling. LatteStyledContainer keeps its collapsed state in
 * localStorage under `<id>-collapsed` through useStorage. Writing localStorage directly and dispatching VueUse's
 * same-tab storage event updates the mounted container without creating a useStorage ref (and its window listeners)
 * outside any component scope. `containerId` is the container to expand when the target sits inside another section.
 * Returns false when the target is not on the page.
 */
export async function expandElement(id, containerId = id) {
    if (!document.getElementById(id)) {
        return false
    }
    writeStorageAndNotify(`${containerId}-collapsed`, 'false')
    await nextTick()

    return true
}

/**
 * Scrolls to a section like util.scrollToElement, first expanding its collapsible container (expandElement).
 * Returns false when the target is not on the page.
 */
export async function expandAndScrollToElement(id, containerId = id) {
    if (!(await expandElement(id, containerId))) {
        return false
    }
    util.scrollToElement(id)

    return true
}

function writeStorageAndNotify(key, newValue) {
    try {
        const oldValue = localStorage.getItem(key)
        if (oldValue === newValue) {
            return
        }
        localStorage.setItem(key, newValue)
        window.dispatchEvent(new CustomEvent(customStorageEventName, { detail: { key, oldValue, newValue, storageArea: localStorage } }))
    } catch {
        return
    }
}

export function debounce(func, delay) {
    let timer = null;
    return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => {
        func.apply(this, args);
        }, delay);
    };
}

export function isAttributeEqual(obj1, obj2, key) {
    if (((typeof obj1[key] === 'object' && obj1[key] !== null) || Array.isArray(obj1[key])) ||
        ((typeof obj2[key] === 'object' && obj2[key] !== null) || Array.isArray(obj2[key]))) {
        return JSON.stringify(obj1[key]) == JSON.stringify(obj2[key])
    }

    return obj1[key] == obj2[key]
}

export function getModifiedProperties(newValue, oldValue, fields) {
    const object1 = JSON.parse(newValue)
    const object2 = JSON.parse(oldValue)

    function findDifferences(newObject, oldObject, fields) {
        const diff = {};

        for (const i in fields) {
            const key = (typeof fields[i] !== 'object') ? fields[i] : fields[i]['key']
            const diffFunc = (typeof fields[i] !== 'object') ? null : fields[i]['diff']
            const sortFunc = (typeof fields[i] !== 'object') ? null : fields[i]['sort']

            if (typeof newObject[key] === 'object' && newObject[key] !== null && !Array.isArray(newObject[key])) {
                if (diffFunc) {
                    const diffResult = diffFunc(newObject[key], oldObject[key])
                    if (diffResult) {
                        diff[key] = diffResult
                    }
                }
            // Be careful as we only use none strict compare
            } else if (!Array.isArray(newObject[key]) && newObject[key] != oldObject[key]) {
                diff[key] = newObject[key]
            } else if (Array.isArray(newObject[key])) {
                if (diffFunc) {
                    const diffResult = diffFunc(newObject[key], oldObject[key])
                    if (diffResult) {
                        diff[key] = diffResult
                    }
                } else {
                    if (arraysHaveSameItems(newObject[key], oldObject[key], sortFunc) === false) {
                        diff[key] = newObject[key];
                    }
                }
            }

        }

        return diff;
    }

    function arraysHaveSameItems(arr1, arr2, sortFunc) {
        if (arr1.length !== arr2.length) return false;

        const sortedArr1 = sortFunc ? arr1.slice().sort(sortFunc) : arr1.slice().sort();
        const sortedArr2 = sortFunc ? arr2.slice().sort(sortFunc) : arr2.slice().sort();

        for (let i = 0; i < sortedArr1.length; i++) {
            if (sortedArr1[i] !== null && sortedArr2[i] !== null && typeof(sortedArr1[i]) === 'object' && typeof(sortedArr2[i]) === 'object') {
                if (Object.keys(findDifferences(sortedArr1[i], sortedArr2[i], Object.keys(sortedArr1[i]))).length > 0 ||
                    Object.keys(findDifferences(sortedArr2[i], sortedArr1[i], Object.keys(sortedArr2[i]))).length > 0) {
                    return false;
                }
            } else if (sortedArr1[i] !== sortedArr2[i]) {
                return false;
            }
        }

        return true;
    }

    return findDifferences(object1, object2, fields);
}

export function same(a, b) {
    return JSON.stringify(a) === JSON.stringify(b)
}

export function deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj))
}

export function getOptionName(id, array) {
    const index = array.findIndex(item => item.id == id);
    return index == -1 ? undefined :  array[index].name;
}

export function fullName(first_name, last_name) {
    return [first_name, last_name].filter(Boolean).join(' ').trim()
}

export function isAddressComplete(address, address_2, city, state, zip) {
    return [address, city, state, zip].every(Boolean);
}

export function handleBlur() {
    if (typeof window !== 'undefined') {
        const activeElement = document.activeElement
        activeElement?.blur?.()
    }
}

export function inchesToFeetAndInches(inches, outputFormat = 'object', zeroPlaceholder = '&mdash;') {
    const feet = inches ? Math.floor(inches / 12) : null;
    const inch = inches ? inches % 12 : null;

    if (outputFormat === 'string') {
        return inches ? `${feet}' ${inch}"` : zeroPlaceholder;
    }

    return {
        foot: feet,
        inch: inch
    };
}
